import { AnimatePresence, motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import React, { useState } from 'react';

interface TrendDataPoint {
  month: string;
  created: number;
  closedWon: number;
  closedLost: number;
  isProjection?: boolean;
  isPartial?: boolean;
  actualData?: {
    created: number;
    closedWon: number;
    closedLost: number;
  };
}

interface TrendLineChartProps {
  data: TrendDataPoint[];
  title: string;
}

interface TooltipData {
  month: string;
  created: number;
  closedWon: number;
  closedLost: number;
  x: number;
  y: number;
  isProjection?: boolean;
  isPartial?: boolean;
  actualData?: {
    created: number;
    closedWon: number;
    closedLost: number;
  };
}

export const TrendLineChart: React.FC<TrendLineChartProps> = ({ data, title }) => {
  const [hoveredPoint, setHoveredPoint] = useState<TooltipData | null>(null);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No trend data available</p>
        </div>
      </div>
    );
  }

  // Calculate max value for scaling
  const maxValue = Math.max(...data.map(d => Math.max(d.created, d.closedWon, d.closedLost)));
  const yAxisMax = Math.ceil(maxValue * 1.2); // 20% padding

  // Format month labels (e.g., "2025-01" -> "Jan 2025")
  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Calculate chart dimensions based on number of data points
  const minMonthWidth = 120; // Increased spacing between months for clarity
  const baseWidth = 800;
  const calculatedWidth = Math.max(baseWidth, data.length * minMonthWidth + 200);
  const chartWidth = calculatedWidth;
  const chartHeight = 400;
  const padding = { top: 40, right: 60, bottom: 60, left: 60 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate points for each line
  const calculatePoints = (values: number[]) => {
    return values.map((value, index) => {
      const x = padding.left + (index / (data.length - 1)) * innerWidth;
      const y = padding.top + innerHeight - (value / yAxisMax) * innerHeight;

      return { x, y, value };
    });
  };

  const createdPoints = calculatePoints(data.map(d => d.created));
  const closedWonPoints = calculatePoints(data.map(d => d.closedWon));
  const closedLostPoints = calculatePoints(data.map(d => d.closedLost));

  // Find index where projections start
  const firstProjectionIndex = data.findIndex(d => d.isProjection);
  const hasProjections = firstProjectionIndex !== -1;

  // Split points into actual and projected segments
  const splitPoints = (points: { x: number; y: number; value: number }[]) => {
    if (!hasProjections) return { actual: points, projected: [] };

    return {
      actual: points.slice(0, firstProjectionIndex),
      projected: points.slice(firstProjectionIndex - 1), // Include last actual point for continuity
    };
  };

  const createdSplit = splitPoints(createdPoints);
  const closedWonSplit = splitPoints(closedWonPoints);
  const closedLostSplit = splitPoints(closedLostPoints);

  // Create path string for line
  const createPath = (points: { x: number; y: number }[]) => {
    if (points.length === 0) return '';

    return points.reduce((path, point, index) => {
      if (index === 0) {
        return `M ${point.x} ${point.y}`;
      }

      // Smooth curve using quadratic bezier
      const prevPoint = points[index - 1];
      const midX = (prevPoint.x + point.x) / 2;

      return `${path} Q ${prevPoint.x} ${prevPoint.y}, ${midX} ${(prevPoint.y + point.y) / 2} Q ${midX} ${(prevPoint.y + point.y) / 2}, ${point.x} ${point.y}`;
    }, '');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/20 rounded-xl shadow-md border border-indigo-200 dark:border-indigo-800/30">
          <TrendingUp className="w-6 h-6 text-indigo-600 dark:text-indigo-400" strokeWidth={2.5} />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>

      {/* Chart Container with Horizontal Scroll */}
      <div className="bg-gradient-to-br from-white via-gray-50/30 to-white dark:from-gray-800 dark:via-gray-800/50 dark:to-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-2xl hover:shadow-3xl transition-all duration-300">
        <div className="overflow-x-auto overflow-y-visible scrollbar-thin scrollbar-thumb-indigo-300 dark:scrollbar-thumb-indigo-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800 hover:scrollbar-thumb-indigo-400 dark:hover:scrollbar-thumb-indigo-500">
          <svg
            width={chartWidth}
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="min-w-full"
            style={{ minWidth: `${chartWidth}px` }}
          >
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
              const y = padding.top + innerHeight * (1 - ratio);

              return (
                <g key={`grid-${index}`}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={chartWidth - padding.right}
                    y2={y}
                    stroke="currentColor"
                    strokeWidth={index === 0 ? '2' : '1'}
                    strokeDasharray={index === 0 ? 'none' : '4 4'}
                    className={
                      index === 0
                        ? 'text-gray-300 dark:text-gray-600'
                        : 'text-gray-200 dark:text-gray-700'
                    }
                  />
                  <text
                    x={padding.left - 15}
                    y={y + 5}
                    textAnchor="end"
                    className="text-sm font-semibold fill-gray-600 dark:fill-gray-400"
                  >
                    {Math.round(yAxisMax * ratio)}
                  </text>
                </g>
              );
            })}

            {/* X-axis labels */}
            {data.map((d, index) => {
              const x = padding.left + (index / (data.length - 1)) * innerWidth;

              return (
                <g key={`label-${index}`}>
                  <text
                    x={x}
                    y={chartHeight - padding.bottom + 25}
                    textAnchor="middle"
                    className={`text-sm font-semibold ${d.isProjection ? 'fill-amber-500 dark:fill-amber-400' : 'fill-gray-700 dark:fill-gray-300'}`}
                  >
                    {formatMonth(d.month)}
                  </text>
                  {d.isProjection && (
                    <text
                      x={x}
                      y={chartHeight - padding.bottom + 40}
                      textAnchor="middle"
                      className="text-xs fill-amber-500 dark:fill-amber-400"
                      style={{ fontSize: '10px' }}
                    >
                      (Proj.)
                    </text>
                  )}
                </g>
              );
            })}

            {/* Line 1: Created (Blue) - Actual Data */}
            <motion.path
              d={createPath(createdSplit.actual)}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
              className="drop-shadow-sm"
            />

            {/* Line 1: Created (Blue) - Projected Data */}
            {hasProjections && (
              <motion.path
                d={createPath(createdSplit.projected)}
                fill="none"
                stroke="#3B82F6"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="8 6"
                opacity="0.6"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.3 }}
                className="drop-shadow-sm"
              />
            )}

            {createdPoints.map((point, index) => (
              <g key={`created-${index}`}>
                {/* Invisible larger hit area for better hover detection */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="20"
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() =>
                    setHoveredPoint({
                      month: data[index].month,
                      created: data[index].created,
                      closedWon: data[index].closedWon,
                      closedLost: data[index].closedLost,
                      x: point.x,
                      y: point.y,
                      isProjection: data[index].isProjection,
                      isPartial: data[index].isPartial,
                      actualData: data[index].actualData,
                    })
                  }
                  onMouseLeave={() => setHoveredPoint(null)}
                />
                {/* Visible circle */}
                <motion.circle
                  cx={point.x}
                  cy={point.y}
                  r={hoveredPoint?.month === data[index].month ? 10 : 7}
                  fill={data[index].isProjection ? 'none' : '#3B82F6'}
                  stroke={data[index].isProjection ? '#3B82F6' : 'white'}
                  strokeWidth={data[index].isProjection ? '2' : '3'}
                  opacity={data[index].isProjection ? 0.6 : 1}
                  strokeDasharray={data[index].isProjection ? '3 2' : 'none'}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  className="drop-shadow-md pointer-events-none"
                />
                {hoveredPoint?.month === data[index].month && (
                  <motion.circle
                    cx={point.x}
                    cy={point.y}
                    r="14"
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    opacity="0.3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </g>
            ))}

            {/* Line 2: Closed Won (Green) - Actual Data */}
            <motion.path
              d={createPath(closedWonSplit.actual)}
              fill="none"
              stroke="#10B981"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.2 }}
              className="drop-shadow-sm"
            />

            {/* Line 2: Closed Won (Green) - Projected Data */}
            {hasProjections && (
              <motion.path
                d={createPath(closedWonSplit.projected)}
                fill="none"
                stroke="#10B981"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="8 6"
                opacity="0.6"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.5 }}
                className="drop-shadow-sm"
              />
            )}

            {closedWonPoints.map((point, index) => (
              <g key={`won-${index}`}>
                {/* Invisible larger hit area for better hover detection */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="20"
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() =>
                    setHoveredPoint({
                      month: data[index].month,
                      created: data[index].created,
                      closedWon: data[index].closedWon,
                      closedLost: data[index].closedLost,
                      x: point.x,
                      y: point.y,
                      isProjection: data[index].isProjection,
                      isPartial: data[index].isPartial,
                      actualData: data[index].actualData,
                    })
                  }
                  onMouseLeave={() => setHoveredPoint(null)}
                />
                {/* Visible circle */}
                <motion.circle
                  cx={point.x}
                  cy={point.y}
                  r={hoveredPoint?.month === data[index].month ? 10 : 7}
                  fill={data[index].isProjection ? 'none' : '#10B981'}
                  stroke={data[index].isProjection ? '#10B981' : 'white'}
                  strokeWidth={data[index].isProjection ? '2' : '3'}
                  opacity={data[index].isProjection ? 0.6 : 1}
                  strokeDasharray={data[index].isProjection ? '3 2' : 'none'}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
                  className="drop-shadow-md pointer-events-none"
                />
                {hoveredPoint?.month === data[index].month && (
                  <motion.circle
                    cx={point.x}
                    cy={point.y}
                    r="14"
                    fill="none"
                    stroke="#10B981"
                    strokeWidth="2"
                    opacity="0.3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </g>
            ))}

            {/* Line 3: Closed Lost (Red) - Actual Data */}
            <motion.path
              d={createPath(closedLostSplit.actual)}
              fill="none"
              stroke="#EF4444"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.4 }}
              className="drop-shadow-sm"
            />

            {/* Line 3: Closed Lost (Red) - Projected Data */}
            {hasProjections && (
              <motion.path
                d={createPath(closedLostSplit.projected)}
                fill="none"
                stroke="#EF4444"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="8 6"
                opacity="0.6"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.7 }}
                className="drop-shadow-sm"
              />
            )}

            {closedLostPoints.map((point, index) => (
              <g key={`lost-${index}`}>
                {/* Invisible larger hit area for better hover detection */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="20"
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() =>
                    setHoveredPoint({
                      month: data[index].month,
                      created: data[index].created,
                      closedWon: data[index].closedWon,
                      closedLost: data[index].closedLost,
                      x: point.x,
                      y: point.y,
                      isProjection: data[index].isProjection,
                      isPartial: data[index].isPartial,
                      actualData: data[index].actualData,
                    })
                  }
                  onMouseLeave={() => setHoveredPoint(null)}
                />
                {/* Visible circle */}
                <motion.circle
                  cx={point.x}
                  cy={point.y}
                  r={hoveredPoint?.month === data[index].month ? 10 : 7}
                  fill={data[index].isProjection ? 'none' : '#EF4444'}
                  stroke={data[index].isProjection ? '#EF4444' : 'white'}
                  strokeWidth={data[index].isProjection ? '2' : '3'}
                  opacity={data[index].isProjection ? 0.6 : 1}
                  strokeDasharray={data[index].isProjection ? '3 2' : 'none'}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.3 }}
                  className="drop-shadow-md pointer-events-none"
                />
                {hoveredPoint?.month === data[index].month && (
                  <motion.circle
                    cx={point.x}
                    cy={point.y}
                    r="14"
                    fill="none"
                    stroke="#EF4444"
                    strokeWidth="2"
                    opacity="0.3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </g>
            ))}

            {/* Enhanced Tooltip */}
            <AnimatePresence>
              {hoveredPoint && (
                <motion.g
                  initial={{ opacity: 0, scale: 0.85, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.85, y: 10 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{ pointerEvents: 'none' }}
                >
                  {/* Tooltip Shadow Layer */}
                  <rect
                    x={hoveredPoint.x - 95}
                    y={hoveredPoint.y - (hoveredPoint.isProjection ? 145 : 130)}
                    width="190"
                    height={hoveredPoint.isProjection ? 135 : 115}
                    rx="12"
                    fill="rgba(0, 0, 0, 0.2)"
                    filter="blur(8px)"
                    pointerEvents="none"
                  />

                  {/* Tooltip Background - Outer Border */}
                  <rect
                    x={hoveredPoint.x - 95}
                    y={hoveredPoint.y - (hoveredPoint.isProjection ? 145 : 130)}
                    width="190"
                    height={hoveredPoint.isProjection ? 135 : 115}
                    rx="12"
                    fill="url(#tooltipGradient)"
                    stroke={
                      hoveredPoint.isProjection
                        ? 'rgba(251, 191, 36, 0.8)'
                        : 'rgba(99, 102, 241, 0.6)'
                    }
                    strokeWidth="2.5"
                    pointerEvents="none"
                  />

                  {/* Tooltip Inner Background */}
                  <rect
                    x={hoveredPoint.x - 92}
                    y={hoveredPoint.y - (hoveredPoint.isProjection ? 142 : 127)}
                    width="184"
                    height={hoveredPoint.isProjection ? 129 : 109}
                    rx="10"
                    fill="rgba(15, 23, 42, 0.98)"
                    pointerEvents="none"
                  />

                  {/* Gradient Definition */}
                  <defs>
                    <linearGradient id="tooltipGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(99, 102, 241, 0.3)" />
                      <stop offset="100%" stopColor="rgba(59, 130, 246, 0.1)" />
                    </linearGradient>
                  </defs>

                  {/* Header Section */}
                  <rect
                    x={hoveredPoint.x - 92}
                    y={hoveredPoint.y - (hoveredPoint.isProjection ? 142 : 127)}
                    width="184"
                    height={hoveredPoint.isProjection ? 42 : 32}
                    rx="10"
                    fill={
                      hoveredPoint.isProjection
                        ? 'rgba(251, 191, 36, 0.2)'
                        : 'rgba(99, 102, 241, 0.15)'
                    }
                  />

                  {/* Month Title */}
                  <text
                    x={hoveredPoint.x}
                    y={hoveredPoint.y - (hoveredPoint.isProjection ? 118 : 103)}
                    textAnchor="middle"
                    className="fill-white text-base font-black"
                    style={{ letterSpacing: '0.5px' }}
                  >
                    {formatMonth(hoveredPoint.month)}
                  </text>

                  {/* Projection Badge */}
                  {hoveredPoint.isProjection && (
                    <text
                      x={hoveredPoint.x}
                      y={hoveredPoint.y - 103}
                      textAnchor="middle"
                      className="fill-amber-400 text-xs font-extrabold"
                      style={{ letterSpacing: '1px' }}
                    >
                      PROJECTED
                    </text>
                  )}

                  {/* Divider Line */}
                  <line
                    x1={hoveredPoint.x - 82}
                    y1={hoveredPoint.y - (hoveredPoint.isProjection ? 97 : 92)}
                    x2={hoveredPoint.x + 82}
                    y2={hoveredPoint.y - (hoveredPoint.isProjection ? 97 : 92)}
                    stroke={
                      hoveredPoint.isProjection
                        ? 'rgba(251, 191, 36, 0.4)'
                        : 'rgba(99, 102, 241, 0.3)'
                    }
                    strokeWidth="1.5"
                  />

                  {/* New Clinics Row */}
                  <g>
                    <circle
                      cx={hoveredPoint.x - 75}
                      cy={hoveredPoint.y - (hoveredPoint.isProjection ? 77 : 72)}
                      r="5"
                      fill="#3B82F6"
                      stroke="rgba(59, 130, 246, 0.4)"
                      strokeWidth="2"
                    />
                    <text
                      x={hoveredPoint.x - 62}
                      y={hoveredPoint.y - (hoveredPoint.isProjection ? 73 : 68)}
                      className="fill-gray-300 text-xs font-medium"
                    >
                      New Entered
                    </text>
                    <text
                      x={hoveredPoint.x + 80}
                      y={hoveredPoint.y - (hoveredPoint.isProjection ? 73 : 68)}
                      textAnchor="end"
                      className={
                        hoveredPoint.isProjection
                          ? 'fill-amber-300 text-sm font-black'
                          : 'fill-blue-400 text-sm font-black'
                      }
                    >
                      {hoveredPoint.created}
                      {hoveredPoint.isProjection && ' ~'}
                    </text>
                  </g>

                  {/* Closed Won Row */}
                  <g>
                    <circle
                      cx={hoveredPoint.x - 75}
                      cy={hoveredPoint.y - (hoveredPoint.isProjection ? 57 : 52)}
                      r="5"
                      fill="#10B981"
                      stroke="rgba(16, 185, 129, 0.4)"
                      strokeWidth="2"
                    />
                    <text
                      x={hoveredPoint.x - 62}
                      y={hoveredPoint.y - (hoveredPoint.isProjection ? 53 : 48)}
                      className="fill-gray-300 text-xs font-medium"
                    >
                      Closed Won
                    </text>
                    <text
                      x={hoveredPoint.x + 80}
                      y={hoveredPoint.y - (hoveredPoint.isProjection ? 53 : 48)}
                      textAnchor="end"
                      className={
                        hoveredPoint.isProjection
                          ? 'fill-amber-300 text-sm font-black'
                          : 'fill-green-400 text-sm font-black'
                      }
                    >
                      {hoveredPoint.closedWon}
                      {hoveredPoint.isProjection && ' ~'}
                    </text>
                  </g>

                  {/* Closed Lost Row */}
                  <g>
                    <circle
                      cx={hoveredPoint.x - 75}
                      cy={hoveredPoint.y - (hoveredPoint.isProjection ? 37 : 32)}
                      r="5"
                      fill="#EF4444"
                      stroke="rgba(239, 68, 68, 0.4)"
                      strokeWidth="2"
                    />
                    <text
                      x={hoveredPoint.x - 62}
                      y={hoveredPoint.y - (hoveredPoint.isProjection ? 33 : 28)}
                      className="fill-gray-300 text-xs font-medium"
                    >
                      Closed Lost
                    </text>
                    <text
                      x={hoveredPoint.x + 80}
                      y={hoveredPoint.y - (hoveredPoint.isProjection ? 33 : 28)}
                      textAnchor="end"
                      className={
                        hoveredPoint.isProjection
                          ? 'fill-amber-300 text-sm font-black'
                          : 'fill-red-400 text-sm font-black'
                      }
                    >
                      {hoveredPoint.closedLost}
                      {hoveredPoint.isProjection && ' ~'}
                    </text>
                  </g>

                  {/* Projection Note */}
                  {hoveredPoint.isProjection && (
                    <text
                      x={hoveredPoint.x}
                      y={hoveredPoint.y - 18}
                      textAnchor="middle"
                      className="fill-amber-400 text-xs italic"
                      style={{ fontSize: '10px' }}
                    >
                      Based on 3-month average
                    </text>
                  )}

                  {/* Tooltip Pointer/Arrow */}
                  <path
                    d={`M ${hoveredPoint.x - 8} ${hoveredPoint.y - 15} L ${hoveredPoint.x} ${hoveredPoint.y - 5} L ${hoveredPoint.x + 8} ${hoveredPoint.y - 15} Z`}
                    fill="rgba(15, 23, 42, 0.98)"
                    stroke="rgba(99, 102, 241, 0.6)"
                    strokeWidth="2"
                  />
                </motion.g>
              )}
            </AnimatePresence>
          </svg>
        </div>

        {/* Scroll Hint */}
        {data.length > 8 && (
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500 dark:text-gray-400">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16l-4-4m0 0l4-4m-4 4h18"
              />
            </svg>
            <span>Scroll horizontally to view all months</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center justify-center flex-wrap gap-4 mt-8">
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30">
            <div className="w-5 h-5 rounded-full bg-blue-500 shadow-md"></div>
            <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
              New Clinics Entered
            </span>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30">
            <div className="w-5 h-5 rounded-full bg-green-500 shadow-md"></div>
            <span className="text-sm font-bold text-green-700 dark:text-green-300">Closed Won</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30">
            <div className="w-5 h-5 rounded-full bg-red-500 shadow-md"></div>
            <span className="text-sm font-bold text-red-700 dark:text-red-300">Closed Lost</span>
          </div>

          {/* Projection Legend */}
          {hasProjections && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border-2 border-dashed border-amber-300 dark:border-amber-700/50">
              <svg width="24" height="24" viewBox="0 0 24 24" className="flex-shrink-0">
                <line
                  x1="2"
                  y1="12"
                  x2="22"
                  y2="12"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="6 4"
                  className="text-amber-500"
                />
                <circle
                  cx="8"
                  cy="12"
                  r="3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-amber-500"
                />
                <circle
                  cx="16"
                  cy="12"
                  r="3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-amber-500"
                />
              </svg>
              <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                Projected Data
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
